import { ReloadOutlined } from "@ant-design/icons";
import { Button, Card, Table, Tag, Typography } from "antd";
import mergeClassNames from "common/mergeClassNames";
import React from "react";

const { Title, Text } = Typography;

interface AiMappingDataPreviewProps {
    data: Record<string, any>[];
    columns: Array<{
        title: string;
        dataIndex: string;
        key: string;
        dataType?: string;
        description?: string;
    }>;
    loading?: boolean;
    onRefresh?: () => void;
    className?: string;
}

const AiMappingDataPreview: React.FC<AiMappingDataPreviewProps> = ({ data = [], columns = [], loading = false, onRefresh, className = "" }) => {
    const tableColumns = columns.map((col) => ({
        ...col,
        render: (value: any, record: any, index: number) => {
            if (value === null || value === undefined) {
                return <span className="text-gray-400">-</span>;
            }

            // Format the value based on data type
            let displayValue = value;
            if (col.dataType === "Float" || col.dataType === "Integer") {
                displayValue = Number(value).toFixed(col.dataType === "Float" ? 1 : 0);
            }

            return (
                <div className="flex flex-col">
                    <span className="text-gray-900">{displayValue}</span>
                    {col.description && <Tag className="border-gray-200 bg-gray-50 text-xs text-gray-500">{col.description}</Tag>}
                </div>
            );
        },
    }));

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                    <Text className="text-gray-500">Loading preview...</Text>
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="flex items-center justify-center p-8 text-gray-500">
                <Text>No data available for preview</Text>
            </div>
        );
    }

    return (
        <div className={mergeClassNames("ai-mapping-data-preview", className)}>
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <Title level={5} className="mb-0">
                    Mapped Data Preview (First {data.length} Rows)
                </Title>
                {onRefresh && (
                    <Button icon={<ReloadOutlined />} onClick={onRefresh} size="small" type="primary">
                        Refresh Preview
                    </Button>
                )}
            </div>

            {/* Data Table */}
            <Card className="overflow-hidden">
                <Table
                    dataSource={data}
                    columns={tableColumns}
                    pagination={false}
                    size="small"
                    scroll={{ x: "max-content" }}
                    className="ai-mapping-preview-table"
                    rowKey={(record, index) => `row-${index}`}
                />
            </Card>
        </div>
    );
};

export default AiMappingDataPreview;
